import {CssBaseline} from '@material-ui/core';
import {ThemeProvider} from '@material-ui/core/styles';
import LABO_THEME from 'app/theme';
import React from 'react';
import {DialogProvider} from 'hook/DialogProvider';
import {BrowserRouter, Redirect, Route, Switch} from 'react-router-dom';
import {UserInfoProvider, useUserInfo} from 'hook/UseUserInfo';
import {LocalesProvider} from 'hook/LocalesProvider';
import {SnackbarProvider} from 'notistack';
import {ErrorBoundary} from 'app/ErrorBoundary';
import {ROUTES} from 'utils/constants/routes';
import {ErrorPage} from 'pages/ErrorPage';
import {LaboratorySelectionPage} from 'pages/LaboratorySelectionPage';
import {CookiesProvider} from 'react-cookie';
import {SubRoutes} from 'routes/SubRoutes';
import Header from 'components/Header/Header';
import {LaboratoryInfoProvider} from 'hook/UseLaboratoryInfo';
import ImpersonatingUserOverlay from 'components/ImpersonatingUserOverlay/ImpersonatingUserOverlay';
import {AccountPage} from 'pages/AccountPage';
import usePageTracking from 'hook/usePageTracking';
import {AuthProvider, AuthRequiredSection} from "hook/AuthProvider";
import {I18NProvider, useI18NContext} from "hook/I18nProvider";
import {Spinner} from "commons/Spinner/Spinner";
import {NoteProvider} from "hook/NoteProvider";
import { PrivateDownloadFilePage, PublicDownloadFilePage } from 'pages/DownloadFilePage';

const MainAppPage = () => {
  const { role, connectedAsUser } = useUserInfo();
  usePageTracking();

  return (
    role !== undefined ?
      <>
        <Header/>
        <SubRoutes/>
        {connectedAsUser && <ImpersonatingUserOverlay/>}
      </> : null
  );
};

const MainApp = () => (
  <UserInfoProvider>
    <LocalesProvider>
      <LaboratoryInfoProvider>
        <NoteProvider>
          <MainAppPage/>
        </NoteProvider>
      </LaboratoryInfoProvider>
    </LocalesProvider>
  </UserInfoProvider>
);

const NonAuthenticatedRoute = ({component, path}) => {
  return <Route component={component} path={path}/>
}

const AuthenticatedRoute = ({component, path}) => {
  const { translationsLoaded } = useI18NContext();

  return (
    <Route path={path}>
      <AuthRequiredSection>
        {translationsLoaded ? (
          <Route component={component}/>
        ) : (
          <Spinner/>
        )}
      </AuthRequiredSection>
    </Route>
  )
}

const MainRouter = () => {
  return (
    <Switch>
      {/* Match no auth routes first */}
      <NonAuthenticatedRoute component={ErrorPage} path={ROUTES.ERROR}/>
      <NonAuthenticatedRoute component={PublicDownloadFilePage} path={ROUTES.DOWNLOAD.PUBLIC}/>

      {/* Match auth routes after */}
      <AuthenticatedRoute component={PrivateDownloadFilePage} path={ROUTES.DOWNLOAD.PRIVATE}/>
      <AuthenticatedRoute component={AccountPage} path={ROUTES.GENERAL_ACCOUNT.BASE}/>
      <AuthenticatedRoute component={LaboratorySelectionPage} path={ROUTES.CHOICE}/>
      <AuthenticatedRoute component={MainApp} path={ROUTES.BASE}/>
      <Redirect to={ROUTES.CHOICE}/>
    </Switch>
  )
}

const App = () => {
  return (
    <ThemeProvider theme={(LABO_THEME)}>
      <CssBaseline>
        <BrowserRouter>
          <AuthProvider>
            <I18NProvider>
              <CookiesProvider>
                <DialogProvider>
                  <SnackbarProvider maxSnack={3}>
                    <ErrorBoundary>
                      <MainRouter/>
                    </ErrorBoundary>
                  </SnackbarProvider>
                </DialogProvider>
              </CookiesProvider>
            </I18NProvider>
          </AuthProvider>
        </BrowserRouter>
      </CssBaseline>
    </ThemeProvider>
  );
};
export default App;